import { ROLES, STAGES } from "components/common/Constants";
import React, { Component } from "react";
import { useState } from "react";
import styled from "styled-components";

export default function ShowUpdateCount({ cards, selectedStage = null }) {
  // Map selected stage to role for filtering
  const getRoleFromSelectedStage = (stage) => {
    if (!stage) return null;
    const stageToRoleMap = {
      designer: ROLES.DESIGN,
      cutting: ROLES.CUTTING,
      secendory_process: ROLES.SECONDARY_PROCESS,
      embroidary: ROLES.EMBROIDARY,
      production: ROLES.PRODUCTION,
      packing: ROLES.PACKING,
      account: ROLES.ACCOUNT, // account maps to accountent role
      payment: ROLES.PAYMENT,
      dispatch: ROLES.DISPATCH,
      creation: null, // Creation doesn't have a role in ShowUpdateCount
      admin: null, // Admin doesn't have a role in ShowUpdateCount
    };
    return stageToRoleMap[stage] || null;
  };

  const getUpdateDateFromRole = (role) => {
    switch (role) {
      case ROLES.DESIGN:
        return "designer_update_date";
      case ROLES.CUTTING:
        return "cutting_update_date";

      case ROLES.SECONDARY_PROCESS:
        return "secendory_update_date";

      case ROLES.EMBROIDARY:
        return "emboridary_update_date";

      case ROLES.PRODUCTION:
        return "production_update_date";

      case ROLES.PACKING:
        return "packing_update_date";

      case ROLES.ACCOUNT:
        return "accountent_update_date";

      case ROLES.PAYMENT:
        return "payment_update_date";

      case ROLES.DISPATCH:
        return "dispatch_update_date";

      default:
        return "";
    }
  };

  const countUpdatedCards = (role) => {
    // If a stage is selected, only count cards for that specific role
    if (selectedStage && selectedStage !== null && selectedStage !== undefined && selectedStage !== '') {
      const selectedRole = getRoleFromSelectedStage(selectedStage);
      // If this is not the selected role, return 0
      if (selectedRole && role !== selectedRole) {
        return 0;
      }
      // If this is the selected role, count ALL cards (they all belong to this stage filter)
      if (selectedRole && role === selectedRole) {
        return cards?.length || 0;
      }
    }
    
    // If no stage selected, count normally
    const _countUpdatedCards = cards?.filter(
      (card) => card[getUpdateDateFromRole(role)]
    );
    
    return _countUpdatedCards?.length || 0;
  };

  const countUpdatedQuantity = (role) => {
    // If a stage is selected, only count quantity for that specific role
    if (selectedStage && selectedStage !== null && selectedStage !== undefined && selectedStage !== '') {
      const selectedRole = getRoleFromSelectedStage(selectedStage);
      // If this is not the selected role, return 0
      if (selectedRole && role !== selectedRole) {
        return 0;
      }
      // If this is the selected role, sum ALL cards' quantities
      if (selectedRole && role === selectedRole) {
        return cards?.reduce((sum, card) => sum + (parseInt(card.quantity) || 0), 0) || 0;
      }
    }
    
    // If no stage selected, count normally
    let quantity = 0;
    cards
      ?.filter((card) => card[getUpdateDateFromRole(role)])
      ?.forEach((card) => {
        quantity += parseInt(card.quantity || 0);
      });
    return quantity;
  };

  // Calculate total quantity based on selected stage
  const countQuant = () => {
    // If a stage is selected, only count quantity for that stage
    if (selectedStage && selectedStage !== null && selectedStage !== undefined && selectedStage !== '') {
      // All cards belong to the selected stage filter
      const filteredItems = cards?.filter((item) => {
        return item.current_stage !== "completed";
      });
      return filteredItems?.reduce((totalQuantity, filteredItem) => {
        return (parseInt(filteredItem.quantity) || 0) + totalQuantity;
      }, 0) || 0;
    }
    
    // If no stage selected, count all non-completed items
    const filteredItems = cards?.filter((item) => {
      return item.current_stage !== "completed";
    });
    return filteredItems?.reduce((totalQuantity, filteredItem) => {
      return (parseInt(filteredItem.quantity) || 0) + totalQuantity;
    }, 0) || 0;
  };

  // Stage data array for rendering
  const stageData = [
    { label: "Design", role: ROLES.DESIGN },
    { label: "Cutting", role: ROLES.CUTTING },
    { label: "Secondary Process", role: ROLES.SECONDARY_PROCESS },
    { label: "Embroidary", role: ROLES.EMBROIDARY },
    { label: "Production", role: ROLES.PRODUCTION },
    { label: "Packing", role: ROLES.PACKING },
    { label: "Billing", role: ROLES.ACCOUNT },
    { label: "Payment", role: ROLES.PAYMENT },
    { label: "Dispatch", role: ROLES.DISPATCH },
  ];

  return (
    <Container>
      <Grid>
        {stageData.map(({ label, role }) => (
          <Card key={label}>
            <StageTitle>{label}</StageTitle>
            <MetricRow>
              <Metric>
                <MetricValue>{countUpdatedCards(role)}</MetricValue>
                <MetricLabel>CARDS</MetricLabel>
              </Metric>
              <Divider />
              <Metric>
                <MetricValue>{countUpdatedQuantity(role)}</MetricValue>
                <MetricLabel>QTY</MetricLabel>
              </Metric>
            </MetricRow>
          </Card>
        ))}

        <TotalCard>
          <StageTitle>TOTAL</StageTitle>
          <MetricRow>
            <Metric>
              <MetricValue $isTotal>{cards?.filter((item) => item.current_stage !== "completed").length || 0}</MetricValue>
              <MetricLabel>CARDS</MetricLabel>
            </Metric>
            <Divider $isTotal />
            <Metric>
              <MetricValue $isTotal>{countQuant()}</MetricValue>
              <MetricLabel>QTY</MetricLabel>
            </Metric>
          </MetricRow>
        </TotalCard>
      </Grid>
    </Container>
  );
}

const Container = styled.div`
  padding: 1.5rem;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 1200px) {
    padding: 1.25rem;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }

  @media (max-width: 576px) {
    padding: 0.75rem;
  }
`;

const Grid = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 0.4rem;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (max-width: 1200px) {
    gap: 0.35rem;
  }

  @media (max-width: 768px) {
    gap: 0.25rem;
  }
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 0.75rem 0.35rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #6366f1, #8b5cf6);
    border-radius: 6px 6px 0 0;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover {
    border-color: #e0e0ff;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(99, 102, 241, 0.08);

    &::before {
      opacity: 1;
    }
  }

  @media (max-width: 992px) {
    padding: 0.65rem 0.3rem;
  }

  @media (max-width: 768px) {
    padding: 0.6rem 0.25rem;
  }
`;

const TotalCard = styled(Card)`
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: none;

  &::before {
    display: none;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(99, 102, 241, 0.25);
    border-color: transparent;
  }
`;

const StageTitle = styled.div`
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #64748b;
  margin-bottom: 0.5rem;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;

  ${TotalCard} & {
    color: rgba(255, 255, 255, 0.95);
  }

  @media (max-width: 992px) {
    font-size: 0.6rem;
    margin-bottom: 0.45rem;
  }

  @media (max-width: 768px) {
    font-size: 0.55rem;
    margin-bottom: 0.4rem;
  }
`;

const MetricRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: 0.35rem;
  width: 100%;

  @media (max-width: 768px) {
    gap: 0.3rem;
  }
`;

const Metric = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const MetricValue = styled.div`
  font-size: ${({ $isTotal }) => ($isTotal ? '1.3rem' : '1.1rem')};
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
  margin-bottom: 0.2rem;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;

  ${TotalCard} & {
    color: #ffffff;
  }

  @media (max-width: 992px) {
    font-size: ${({ $isTotal }) => ($isTotal ? '1.2rem' : '0.95rem')};
  }

  @media (max-width: 768px) {
    font-size: ${({ $isTotal }) => ($isTotal ? '1.1rem' : '0.85rem')};
    margin-bottom: 0.15rem;
  }
`;

const MetricLabel = styled.div`
  font-size: 0.6rem;
  font-weight: 500;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;

  ${TotalCard} & {
    color: rgba(255, 255, 255, 0.8);
  }

  @media (max-width: 768px) {
    font-size: 0.55rem;
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 35px;
  background: ${({ $isTotal }) => 
    $isTotal ? 'rgba(255, 255, 255, 0.3)' : '#e2e8f0'};
  flex-shrink: 0;

  @media (max-width: 768px) {
    height: 28px;
  }
`;
